import { ArrowRight, CheckCircle } from "lucide-react";
import HeroBreadcrumb from "@/components/layout/HeroBreadcrumb";

const AMBITI = [
  {
    titolo: "Sicurezza sul Lavoro",
    desc: "Formazione sui rischi professionali, prevenzione degli infortuni e protezione della salute dei lavoratori, in linea con il D.Lgs. 81/08 e s.m.i. Comprende anche la gestione delle emergenze: incendi, evacuazioni e primo soccorso.",
  },
  {
    titolo: "Privacy e Protezione dei Dati",
    desc: "Corsi legati alla protezione dei dati personali, come previsto dal Regolamento Europeo 2016/679 (GDPR).",
  },
  {
    titolo: "Manipolazione degli Alimenti",
    desc: "Alcune professioni, come quelle legate alla manipolazione di alimenti (HACCP), richiedono corsi obbligatori periodici e specifici aggiornamenti.",
  },
];

const BENEFICI = [
  { titolo: "Conformità alle Normative", desc: "Rispetto di tutte le leggi e normative in vigore, evitando sanzioni e contestazioni." },
  { titolo: "Sicurezza e Salute dei Lavoratori", desc: "Riduzione dei rischi di incidenti e tutela della qualità dell'ambiente lavorativo." },
  { titolo: "Aggiornamenti Costanti", desc: "Le aziende restano sempre allineate alle evoluzioni normative del proprio settore." },
  { titolo: "Protezione dell'Azienda", desc: "Riduzione del rischio di responsabilità legali in caso di incidenti, violazioni o audit delle autorità competenti." },
];

const STEPS = [
  { n: "1", titolo: "Consulenza Iniziale", desc: "Analizziamo gli obblighi normativi specifici per il tuo settore e individuiamo la formazione obbligatoria necessaria." },
  { n: "2", titolo: "Pianificazione del Piano Formativo", desc: "Ti assistiamo nella progettazione del piano formativo, selezionando i corsi obbligatori più adatti e in linea con la normativa vigente." },
  { n: "3", titolo: "Accesso ai Finanziamenti", desc: "Ti aiutiamo a sfruttare i fondi per la formazione finanziata, come EBiT Lazio e Fondo For.Te, per ridurre i costi aziendali." },
  { n: "4", titolo: "Erogazione della Formazione", desc: "Coordiniamo l'erogazione dei corsi obbligatori, tenuti da enti accreditati secondo i requisiti di legge." },
  { n: "5", titolo: "Rendicontazione e Certificazione", desc: "Al termine dei corsi raccogliamo la documentazione per la rendicontazione e l'emissione delle certificazioni finali." },
];

const BENEFICI_DIRETTI = [
  "Adempimento puntuale di tutti gli obblighi di legge",
  "Riduzione del rischio di sanzioni e contenziosi",
  "Maggior sicurezza e tutela per dipendenti e azienda",
  "Costi contenuti grazie ai fondi interprofessionali",
];

export default function FormazioneObbligatoriaPage() {
  return (
    <main id="main-content" data-testid="formazione-obbligatoria-page">
      {/* ── HERO ── */}
      <section className="relative h-[300px] md:h-[360px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1920&q=60&auto=format"
          alt="Formazione obbligatoria"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4, 30, 66, 0.85)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full flex flex-col justify-end pb-10">
          <HeroBreadcrumb />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed'] mb-2">Formazione</span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight">Formazione Obbligatoria</h1>
        </div>
      </section>

      {/* ── INTRO ── */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-lg text-[var(--confcom-text-primary)] font-semibold font-['Encode_Sans_Semi_Condensed'] leading-relaxed mb-5">
            Formazione Obbligatoria: adempi agli obblighi di legge con il supporto di Promo.Ter e Confcommercio Roma.
          </p>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-4">
            La <strong className="text-[var(--confcom-text-primary)]">formazione obbligatoria</strong> è un elemento cruciale per tutte le aziende che operano nel Settore Terziario: consente di soddisfare gli obblighi legali previsti dalle normative nazionali e internazionali e di tutelare al tempo stesso dipendenti e impresa.
          </p>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed">
            Comprende tutti i corsi e gli aggiornamenti che le aziende devono erogare per rispettare le disposizioni legislative in materia di <strong className="text-[var(--confcom-text-primary)]">Sicurezza sul lavoro, Privacy GDPR, HACCP - Igiene alimentare</strong> e altre normative settoriali.
          </p>
        </div>
      </section>

      {/* ── AMBITI ── */}
      <section className="py-16 bg-[#EBF5FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-3">Gli ambiti della Formazione Obbligatoria</h2>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-8 max-w-3xl">
            I corsi obbligatori coprono diverse aree previste dalla legge. Ecco le principali:
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {AMBITI.map((a) => (
              <div key={a.titolo} className="bg-white rounded-xl p-6 border border-[var(--confcom-border)]">
                <h3 className="text-base font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-2">{a.titolo}</h3>
                <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BENEFICI ── */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-8">I Benefici della Formazione Obbligatoria</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {BENEFICI.map((b) => (
              <div key={b.titolo} className="bg-[var(--confcom-bg-subtle)] rounded-xl p-6 border border-[var(--confcom-border)]">
                <h3 className="text-base font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-2">{b.titolo}</h3>
                <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COME FUNZIONA + BENEFICI DIRETTI ── */}
      <section className="py-16 bg-[#EBF5FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-8">Come Funziona con Promo.Ter e Confcommercio Roma</h2>
              <div className="space-y-10">
                {STEPS.map((s) => (
                  <div key={s.n} className="flex gap-4">
                    <div className="w-8 h-8 bg-[var(--confcom-blue)] rounded-full flex items-center justify-center shrink-0 text-white font-bold text-sm font-['Encode_Sans_Semi_Condensed']">{s.n}</div>
                    <div>
                      <h3 className="text-lg font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-1">{s.titolo}</h3>
                      <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed mb-4">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-[var(--confcom-border)] h-fit">
              <h3 className="text-lg font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-5">Benefici Diretti per la Tua Azienda</h3>
              <div className="space-y-3">
                {BENEFICI_DIRETTI.map((b) => (
                  <div key={b} className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-[var(--confcom-blue)] shrink-0 mt-0.5" />
                    <span className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{b}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA finale (banner standard come le altre pagine Formazione) ── */}
      <section className="py-12 bg-[#EBF5FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[var(--confcom-blue)] rounded-2xl px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-5">
            <div>
              <p className="text-white font-bold text-xl font-['Encode_Sans_Semi_Condensed'] mb-2">Vuoi attivare subito il tuo percorso formativo o accedere a nuove opportunità? Contattaci!</p>
              <div className="text-white/75 text-sm space-y-1">
                <p>
                  <a href="mailto:commerciale.promoter@confcommercioroma.it" className="hover:text-white transition-colors">commerciale.promoter@confcommercioroma.it</a>
                  {" · "}
                  <a href="mailto:areagestione@confcommercioroma.it" className="hover:text-white transition-colors">areagestione@confcommercioroma.it</a>
                </p>
                <p>
                  <a href="tel:+390668437606" className="hover:text-white transition-colors">0668437 606</a>
                  {" · "}
                  <a href="tel:+390668437530" className="hover:text-white transition-colors">0668437 530</a>
                  {" · "}
                  <a href="tel:+390668437227" className="hover:text-white transition-colors">0668437 227</a>
                </p>
              </div>
            </div>
            <a
              href="https://forms.gle/98CnoTiUAdGHJ9e88"
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-7 py-3 rounded-md font-bold text-sm hover:opacity-90 transition-opacity whitespace-nowrap"
              data-testid="cta-formazione-obbligatoria"
            >
              Richiedi maggiori informazioni <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
